import { Navigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { ZenLoader } from "@/components/loading/ZenLoader";
import { LoaderVeil } from "@/components/loading/LoaderVeil";
import { useBlankScreenWatchdog } from "@/lib/blank-screen-watchdog";
import { useCapabilities, type CapabilitiesResult } from "@/hooks/useCapabilities";
import { CATALOG_WRITE } from "@/lib/capabilities";

// The account-type role Sets + predicates are still consumed by ~20 Phase 2/3 surfaces
// (TrackPublicPage*, AlbumsPage, MarketplacePage, playlists, TrackDetails, …). Re-export them here
// unchanged — those surfaces migrate to capabilities in later phases.
export {
  BUYER_ROLES,
  NEGOTIATOR_ROLES,
  SELLER_ROLES,
  isBuyerRole,
  isNegotiatorRole,
  isSellerRole,
} from "@/lib/roles";

/**
 * The guard's loading state. Fills whatever container it is placed in (always
 * `<main>`, inside the app shell) rather than claiming the viewport, so the
 * LoaderVeil that replaces it on the way out lands in exactly the same spot —
 * the mark dissolves where it stood instead of jumping first.
 *
 * `min-h-[60vh]` alongside `flex-1` so it works in both homes: inside the shell's
 * flex column `flex-1` takes over, and on the few surfaces that render it outside a
 * flex parent the min-height still gives it a box to centre in rather than
 * collapsing to nothing.
 *
 * Reduced motion is handled globally in index.css; the local <style> override
 * this used to carry has been dropped along with the hand-rolled markup.
 */
export function GuardLoadingScreen() {
  return (
    <div className="flex min-h-[60vh] flex-1 items-center justify-center bg-background">
      <ZenLoader />
    </div>
  );
}

/**
 * Capability/orientation route guard, gated on the anti-flash `loadedOnce` contract: it renders the
 * loader until the subject (auth + role + org + label) resolves, so the allowed/denied branch never
 * flashes. `allow` is evaluated only once resolved.
 *
 * Pick the predicate by intent:
 *  - ADMISSION surface (e.g. /upload) → a CAPABILITY check (`has(...)`); admin = superset auto-passes.
 *  - ORIENTATION surface (buyer-only, non-negotiator) → an `accountType` check; admin must NOT be
 *    treated as a buyer, so a capability check would be wrong there.
 */
function CapabilityGuard({
  children,
  allow,
  redirectTo = "/tracks",
}: {
  children: React.ReactNode;
  allow: (caps: CapabilitiesResult) => boolean;
  redirectTo?: string;
}) {
  const { user } = useAuth();
  const caps = useCapabilities();
  const contentRef = useBlankScreenWatchdog<HTMLDivElement>(
    "capability-guard",
    caps.loadedOnce
  );

  // Admission decisions wait for a resolved subject — reading `allow` earlier
  // would bounce a user on a subject that has not arrived yet.
  if (caps.loadedOnce) {
    // No session → bounce to landing (ShellGate handles the primary redirect; this is a backstop).
    if (!user) {
      return <Navigate to="/" replace />;
    }
    if (!allow(caps)) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  // One container across both states, so the loader and the veil that replaces it
  // occupy the same box and the mark dissolves where it stood. `relative` is the
  // veil's positioning context: it must cover this region only, never the sidebar
  // that is already on screen.
  //
  // Note what is NOT here: any animation on the content. It mounts at full
  // opacity. Fading it in is what stranded this subtree at opacity 0 whenever the
  // guard re-mounted mid-fade — an invisible page that only a reload cleared.
  return (
    <div ref={contentRef} className="relative flex flex-1 flex-col min-h-0">
      <LoaderVeil ready={caps.loadedOnce} loader={<GuardLoadingScreen />}>
        {children}
      </LoaderVeil>
    </div>
  );
}

// ── Convenience guards ─────────────────────────────────────────────────────────

/** Buyer-only surface (orientation). Admin is NOT a buyer → must not pass. */
export function BuyerGuard({ children }: { children: React.ReactNode }) {
  return (
    <CapabilityGuard allow={(c) => c.subject.accountType === "buyer"} redirectTo="/tracks">
      {children}
    </CapabilityGuard>
  );
}

/** Catalog-write surface (admission). Seller + admin (superset) pass; buyer/negotiator denied. */
export function SellerGuard({ children }: { children: React.ReactNode }) {
  return (
    <CapabilityGuard allow={(c) => c.has(CATALOG_WRITE)} redirectTo="/tracks">
      {children}
    </CapabilityGuard>
  );
}

/**
 * Everyone except negotiators (orientation exclusion): buyer + seller + admin pass. A null/unknown
 * account type is denied (default-deny, matching the original allowedRoles.has() behavior).
 */
export function NonNegotiatorGuard({ children }: { children: React.ReactNode }) {
  return (
    <CapabilityGuard
      allow={(c) => c.subject.accountType != null && c.subject.accountType !== "negotiator"}
      redirectTo="/licenses"
    >
      {children}
    </CapabilityGuard>
  );
}

/** Any resolved account type may participate in licensing (incl. negotiator + admin). */
export function LicensingGuard({ children }: { children: React.ReactNode }) {
  return (
    <CapabilityGuard allow={(c) => c.subject.accountType != null}>
      {children}
    </CapabilityGuard>
  );
}
